import type { Task } from "../types";

export type RecurringChangeTask = Pick<
  Task,
  | "recurrence_frequency"
  | "recurrence_interval"
  | "recurrence_day_of_week"
  | "scheduled_date"
>;

type ConfirmHandler = (task: RecurringChangeTask) => Promise<boolean>;

// Module-level registry so any code path that's about to mutate scheduled_date
// on a recurring task can route through the same warning dialog without having
// to thread the dialog state through every parent in the React tree.
//
// TodoApp registers the actual UI-driven handler on mount; helpers that aren't
// children of TodoApp (e.g., utility functions called from drag handlers) call
// confirmRecurringDateChange() which awaits the handler's promise.
let registeredHandler: ConfirmHandler | null = null;

export function registerRecurringChangeConfirmHandler(
  handler: ConfirmHandler | null,
): void {
  registeredHandler = handler;
}

/**
 * Open the recurring-change warning dialog and resolve with the user's choice.
 * Returns true if the user confirmed, false if they cancelled.
 *
 * Falls back to false (safe) when no handler is registered — this guards
 * against silent surprise re-anchors before the dialog is wired up.
 */
export async function confirmRecurringDateChange(
  task: RecurringChangeTask,
): Promise<boolean> {
  if (!registeredHandler) {
    console.warn(
      "confirmRecurringDateChange called before UI handler was registered",
    );
    return false;
  }
  return registeredHandler(task);
}

/**
 * Persists a scheduled_date/time/duration change via `write`, opening the
 * recurring-change warning first when the change moves `scheduled_date` on a
 * recurring task. A recurring task's day-of-month/day-of-year (and, until
 * its next occurrence, its day-of-week) is read straight off scheduled_date,
 * so moving that date re-anchors the whole series, not just the instance
 * being dragged. A same-day change (e.g. only the time moved) carries no
 * such risk and skips the prompt. `onDeclined` runs if the user cancels, or
 * if the write itself fails — callers use it to revert an optimistic drag.
 */
export function updateGuardedTaskDate(
  task: Task,
  newDate: string,
  newTime: string | null,
  newDuration: number | null,
  write: (task: Task) => Promise<boolean>,
  onDeclined: () => void,
): void {
  const gate =
    task.recurrence_frequency && newDate !== task.scheduled_date
      ? confirmRecurringDateChange(task)
      : Promise.resolve(true);
  gate.then((confirmed) => {
    if (!confirmed) {
      onDeclined();
      return;
    }
    write({
      ...task,
      scheduled_date: newDate,
      scheduled_time: newTime,
      duration: newDuration,
    }).then((applied) => {
      if (!applied) onDeclined();
    });
  });
}
